export interface BrandColor {
  id: number;
  title: string;
  hex: string;
  published?: boolean;
  sortOrder?: number;
}

export type ColorPickerMode = 'system' | 'library';

export interface ColorPolicy {
  mode: ColorPickerMode;
  colors: BrandColor[];
}

export const LEGACY_DESIGN_PALETTE = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
  '#FF00FF', '#00FFFF', '#FF6600', '#6600FF', '#009933', '#CC0066',
  '#333333', '#666666', '#999999', '#CCCCCC', '#FF3333', '#33FF33',
  '#3333FF', '#FFCC00', '#FF33CC', '#33CCFF', '#996633', '#339966',
] as const;

export function normalizeBrandHex(value: unknown): string | null {
  const raw = String(value ?? '').trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw.split('').map((part) => `${part}${part}`).join('').toUpperCase()}`;
  }
  if (!/^[0-9a-f]{6}$/i.test(raw)) return null;
  return `#${raw.toUpperCase()}`;
}

/**
 * Undefined means an older host that does not expose brandPalette yet, so the
 * legacy palette remains available. An explicit empty array means no curated
 * swatches were configured.
 */
export function normalizeBrandPalette(raw: unknown): BrandColor[] {
  if (typeof raw === 'undefined') {
    return LEGACY_DESIGN_PALETTE.map((hex, index) => ({
      id: -(index + 1),
      title: hex,
      hex,
    }));
  }
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const result: BrandColor[] = [];
  raw.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object') return;
    const row = entry as Record<string, unknown>;
    const hex = normalizeBrandHex(row.hex);
    if (!hex || seen.has(hex)) return;
    seen.add(hex);
    result.push({
      id: Number.isFinite(Number(row.id)) ? Number(row.id) : index + 1,
      title: String(row.title ?? hex).trim() || hex,
      hex,
      ...(Object.prototype.hasOwnProperty.call(row, 'published')
        ? { published: row.published === true || Number(row.published) === 1 }
        : {}),
      ...(Number.isFinite(Number(row.sortOrder ?? row.sort_order))
        ? { sortOrder: Number(row.sortOrder ?? row.sort_order) }
        : {}),
    });
  });
  return result;
}

export function normalizeColorPickerMode(raw: unknown): ColorPickerMode {
  return String(raw ?? '').toLowerCase() === 'library' ? 'library' : 'system';
}

export function normalizeColorPolicy(mode: unknown, raw: unknown, publishedOnly = false): ColorPolicy {
  const normalizedMode = normalizeColorPickerMode(mode);
  const colors = normalizeBrandPalette(raw)
    .filter((color) => !publishedOnly || color.published !== false)
    .sort((a, b) => a.sortOrder === undefined && b.sortOrder === undefined
      ? 0
      : (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  return { mode: normalizedMode, colors: normalizedMode === 'library' ? colors : [] };
}

export function colorBelongsToPalette(value: unknown, colors: BrandColor[]): boolean {
  const normalized = normalizeBrandHex(value);
  return normalized !== null && colors.some((color) => color.hex === normalized);
}
